import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsAccessibility from "highcharts/modules/accessibility";
import { useTheme } from "@mui/material/styles";
import { IInsuranceComapyPieChart } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";

// Initialize Highcharts modules
HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsAccessibility(Highcharts);

interface ITaxpopup {
  payrollData: IInsuranceComapyPieChart[];
}

const PayrollDashboard = ({ payrollData }: ITaxpopup) => {
  const classes = useStyles();
  const theme = useTheme();

  // Count policies by categoryName
  const companyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    payrollData.forEach((item) => {
      const name = item.categoryName || "Unknown";
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [payrollData]);

  // Convert counts into Highcharts pie format
  const pieData = useMemo(() => {
    return Object.entries(companyCounts).map(([name, count]) => ({
      name,
      y: count,
    }));
  }, [companyCounts]);

  // Highcharts options for donut chart
  const salaryTrendOptions: Highcharts.Options = useMemo(() => ({
    chart: {
      type: "pie",
      backgroundColor: theme.palette.cardAdd?.bgColor || "#fff",
    },
    title: {
      text: "Policies by Type of Coverage",
      style: {
        fontSize: "0.9375rem",
        color: theme.palette.heading?.headingColor || "#000",
      },
    },
    tooltip: {
      pointFormat: "<b>{point.y} Policies</b> ({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        innerSize: "50%", // This creates the donut effect
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f} %",
          style: {
            color: theme.palette.heading?.headingColor || "#000",
            fontSize: "0.75rem",
          },
        },
      },
    },
    legend: {
      itemStyle: {
        fontSize: "0.75rem",
        color: theme.palette.heading?.headingColor || "#000",
      },
    },
    series: [
      {
        type: "pie",
        name: "Policies",
        data: pieData,
      },
    ],
    credits: { enabled: false },
  }), [pieData, theme]);

  return (
    <div style={{ padding: "5px" }}>
      <HighchartsReact highcharts={Highcharts} options={salaryTrendOptions} />
    </div>
  );
};

export default PayrollDashboard;
